import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useOutletContext } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import LandingPage from '@userPages/LandingPage.jsx';
import HumanVerificationPage from '@userPages/HumanVerificationPage.jsx';
import LoginPage from '@userPages/LoginPage.jsx';
import GoogleAuthCallback from '@userPages/GoogleAuthCallback.jsx';
import RegistrationPage from '@userPages/RegistrationPage.jsx';
import SimpleRegistrationPage from '@userPages/SimpleRegistrationPage.jsx';
import RegistrationSuccessPage from '@userPages/RegistrationSuccessPage.jsx';
import DashboardPage from '@userPages/DashboardPage.jsx';
import DigitalIdPage from '@userPages/DigitalIdPage.jsx';
import DuesTrackingPage from '@userPages/DuesTrackingPage.jsx';
import NewsPage from '@userPages/NewsPage.jsx';
import AccountPage from '@userPages/AccountPage.jsx';
import BenefitsPage from '@userPages/BenefitsPage.jsx';
import MembershipFormPage from '@userPages/MembershipFormPage.jsx';
import RequestAssistancePage from '@userPages/RequestAssistance.jsx';
import MyRequestsPage from '@userPages/MyRequests.jsx';
import ScanQrPage from '@userPages/ScanQrPage.jsx';
import VenueBrowsePage from '@userPages/VenueBrowsePage.jsx';
import VenueDetailPage from '@userPages/VenueDetailPage.jsx';
import MyVenueBookingsPage from '@userPages/MyVenueBookingsPage.jsx';
import NotificationsPage from '@userPages/NotificationsPage.jsx';
import TicketDetailPage from '@userPages/TicketDetailPage.jsx';
import TermsAndConditionsPage from '@userPages/TermsAndConditionsPage.jsx';
import PrivacyPolicyPage from '@userPages/PrivacyPolicyPage.jsx';
import PaymentSuccessPage from '@userPages/PaymentSuccessPage.jsx';
import PaymentCancelPage from '@userPages/PaymentCancelPage.jsx';
import AdminLayout from '@adminComponents/AdminLayout.jsx';
import AdminDashboard from '@adminPages/Dashboard.jsx';
import MembersTable from '@adminPages/MembersTable.jsx';
import RegistrationReview from '@adminPages/RegistrationReview.jsx';
import ProponentQueue from '@adminPages/ProponentQueue.jsx';
import AdminFinalApprovalQueue from '@adminPages/AdminFinalApprovalQueue.jsx';
import TicketDetail from '@adminPages/TicketDetail.jsx';
import Tickets from '@adminPages/Tickets.jsx';
import ReportsAnalytics from '@adminPages/ReportsAnalytics.jsx';
import AuditLog from '@adminPages/AuditLog.jsx';
import AdminNotifications from '@adminPages/AdminNotifications.jsx';
import EventManagement from '@adminPages/EventManagement.jsx';
import VenueManagement from '@adminPages/VenueManagement.jsx';
import ClientBookings from '@adminPages/ClientBookings.jsx';
import IDCardManagement from '@adminPages/IDCardManagement.jsx';
import MemberProfile from '@adminPages/MemberProfile.jsx';
import AdminSettings from '@adminPages/AdminSettings.jsx';
import AdminLogin from '@adminPages/Login.jsx';
import AdminForgotPassword from '@adminPages/ForgotPassword.jsx';
import AdminResetPassword from '@adminPages/ResetPassword.jsx';
import DepartmentLayout from '@deptComponents/DepartmentLayout.jsx';
import DepartmentLogin from '@deptPages/Login.jsx';
import DepartmentTickets from '@deptPages/Tickets.jsx';
import DepartmentTicketDetail from '@deptPages/TicketDetail.jsx';
import ForgotPasswordPage from '@userPages/ForgotPasswordPage.jsx';
import ResetPasswordPage from '@userPages/ResetPasswordPage.jsx';
import ScrollToTopButton from './components/ScrollToTopButton.jsx';
import { updateUserProfile, getUserProfile } from './api/auth';
import client, { BASE_URL, onMemberSessionExpired } from './api/client';
import usePageTitle from './hooks/usePageTitle';
import { setBrandingData } from './utils/unionBranding';
import { ADDRESS_DATA } from './data/luzonAddressData';
import { resolveBackendAssetUrl } from './utils/assetUrl';
// (membership application API still available if needed)
import { mockDues, mockNews } from './data/mockData';

const USER_AUTH_LOCAL_KEY = 'alu_user_auth'; 
const USER_AUTH_SESSION_KEY = 'alu_user_auth_session';
const USER_LEGACY_STORAGE_KEY = 'alu_user';
const USER_LEGACY_TOKEN_KEY = 'alu_token';
const USER_AUTH_SYNC_REQUEST_KEY = 'alu_user_auth_sync_request';
const USER_AUTH_SYNC_RESPONSE_KEY = 'alu_user_auth_sync_response';
const USER_AUTH_SYNC_LOGOUT_KEY = 'alu_user_auth_sync_logout';
const REMEMBER_ME_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_MEMBER_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const MIN_MEMBER_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

const resolveMemberIdleTimeoutMs = () => {
  const raw = Number(process.env.REACT_APP_MEMBER_IDLE_TIMEOUT_MS);
  if (!Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_MEMBER_IDLE_TIMEOUT_MS;
  }
  return Math.max(raw, MIN_MEMBER_IDLE_TIMEOUT_MS);
};

const MEMBER_IDLE_TIMEOUT_MS = resolveMemberIdleTimeoutMs();

const parseAllowedHosts = (...rawValues) => {
  const hosts = rawValues
    .flatMap((value) => String(value ?? '').split(','))
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(hosts));
};

const ADMIN_ALLOWED_HOSTS = parseAllowedHosts(
  process.env.REACT_APP_ADMIN_ALLOWED_HOSTS,
  'localhost',
  '127.0.0.1',
  'laborconnect.app',
  'www.laborconnect.app',
  'admin.laborconnect.app',
);

const isAdminHostAllowed = () => {
  if (typeof window === 'undefined') {
    return true;
  }
  const currentHost = String(window.location?.hostname ?? '').trim().toLowerCase();
  if (!currentHost) {
    return false;
  }
  return ADMIN_ALLOWED_HOSTS.includes(currentHost);
};

const getStoredAdminSession = () => {
  const adminToken = localStorage.getItem('adminToken');
  const adminUserStr = localStorage.getItem('adminUser');

  if (!adminToken || !adminUserStr) {
    return null;
  }

  try {
    const adminUser = JSON.parse(adminUserStr);
    if (!adminUser || typeof adminUser !== 'object') {
      return null;
    }
    return { adminToken, adminUser };
  } catch {
    return null;
  }
};

const clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
};

const clearUserAuthStorage = () => {
  localStorage.removeItem(USER_AUTH_LOCAL_KEY);
  sessionStorage.removeItem(USER_AUTH_SESSION_KEY);
  localStorage.removeItem(USER_LEGACY_STORAGE_KEY);
  localStorage.removeItem(USER_LEGACY_TOKEN_KEY);
};

const restoreStoredUserAuth = () => {
  try {
    const now = Date.now();
    const localRaw = localStorage.getItem(USER_AUTH_LOCAL_KEY);
    if (localRaw) {
      const parsed = JSON.parse(localRaw);
      const expiresAtMs = Number(new Date(parsed?.expiresAt).getTime());
      if (parsed?.user && Number.isFinite(expiresAtMs) && expiresAtMs > now) {
        return {
          user: parsed.user,
          token: parsed.token ?? null,
          rememberMe: true,
          expiresAt: new Date(expiresAtMs).toISOString(),
        };
      }
      localStorage.removeItem(USER_AUTH_LOCAL_KEY);
      localStorage.removeItem(USER_LEGACY_STORAGE_KEY);
      localStorage.removeItem(USER_LEGACY_TOKEN_KEY);
    }

    const sessionRaw = sessionStorage.getItem(USER_AUTH_SESSION_KEY);
    if (sessionRaw) {
      const parsed = JSON.parse(sessionRaw);
      if (parsed?.user) {
        return {
          user: parsed.user,
          token: parsed.token ?? null,
          rememberMe: false,
          expiresAt: null,
        };
      }
      sessionStorage.removeItem(USER_AUTH_SESSION_KEY);
    }

    const legacyRaw = localStorage.getItem(USER_LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacyUser = JSON.parse(legacyRaw);
      if (legacyUser) {
        return {
          user: legacyUser,
          token: localStorage.getItem(USER_LEGACY_TOKEN_KEY) || null,
          rememberMe: false,
          expiresAt: null,
        };
      }
    }
  } catch (_error) {
    clearUserAuthStorage();
  }

  return {
    user: null,
    token: null,
    rememberMe: false,
    expiresAt: null,
  };
};

const persistUserAuth = ({ user, token = null, rememberMe = false, expiresAt = null }) => {
  if (!user) {
    clearUserAuthStorage();
    return;
  }

  if (rememberMe) {
    const resolvedExpiresAt = (() => {
      const parsed = Number(new Date(expiresAt).getTime());
      if (Number.isFinite(parsed) && parsed > Date.now()) {
        return new Date(parsed).toISOString();
      }
      return new Date(Date.now() + REMEMBER_ME_DURATION_MS).toISOString();
    })();

    const payload = {
      user,
      token,
      rememberMe: true,
      expiresAt: resolvedExpiresAt,
    };

    localStorage.setItem(USER_AUTH_LOCAL_KEY, JSON.stringify(payload));
    sessionStorage.removeItem(USER_AUTH_SESSION_KEY);
    localStorage.setItem(USER_LEGACY_STORAGE_KEY, JSON.stringify(user));
    if (token) {
      localStorage.setItem(USER_LEGACY_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(USER_LEGACY_TOKEN_KEY);
    }
    return;
  }

  const payload = {
    user,
    token,
    rememberMe: false,
    expiresAt: null,
  };

  sessionStorage.setItem(USER_AUTH_SESSION_KEY, JSON.stringify(payload));
  localStorage.removeItem(USER_AUTH_LOCAL_KEY);
  localStorage.removeItem(USER_LEGACY_STORAGE_KEY);
  localStorage.removeItem(USER_LEGACY_TOKEN_KEY);
};

const serializeAuthState = ({ user, token = null, rememberMe = false, expiresAt = null }) =>
  JSON.stringify({
    userId: user?.id ?? null,
    token: token ?? null,
    rememberMe: Boolean(rememberMe),
    expiresAt: expiresAt ?? null,
  });

const toDateParts = (value) => {
  if (!value) return { month: '', day: '', year: '' };

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return { month, day, year };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { month: '', day: '', year: '' };
  }

  return {
    month: String(parsed.getUTCMonth() + 1).padStart(2, '0'),
    day: String(parsed.getUTCDate()).padStart(2, '0'),
    year: String(parsed.getUTCFullYear()),
  };
};

const mapUserToRegistrationInitialData = (user) => {
  if (!user) return null;

  let inferredEmergencyRegion = user.emergencyContact?.region || user.emergencyContactRegion || '';
  const emergencyProvince = user.emergencyContact?.province || user.emergencyContactProvince || '';
  if (!inferredEmergencyRegion && emergencyProvince) {
    for (const [reg, provinces] of Object.entries(ADDRESS_DATA)) {
      if (Object.keys(provinces).includes(emergencyProvince)) {
        inferredEmergencyRegion = reg;
        break;
      }
    }
  }

  return {
    firstName: user.firstName || '',
    middleInitial: user.middleInitial || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',

    region: user.region || '',
    address1: user.address_line1 || '',
    address2: user.address_line2 || '',
    barangay: user.barangay || user.address_line2 || '',
    city: user.city || '',
    province: user.province || '',
    postalCode: user.postal_code || '',

    maritalStatus: user.maritalStatus || '',
    numberOfChildren: user.childrenCount ?? '',
    gender: user.gender || '',
    otherGender: user.otherGender || '',
    religion: user.religion || '',
    education: user.education || '',
    placeOfBirth: user.placeOfBirth || '',

    company: user.company || '',
    position: user.position || '',
    department: user.department || '',
    yearsEmployed: user.yearsEmployed ?? '',
    unionAffiliation: user.unionAffiliation || '',
    unionPosition: user.unionPosition || '',

    dateOfBirth: toDateParts(user.dateOfBirth),

    emergencyContact: {
      name: user.emergencyContact?.name || user.emergencyContactName || '',
      relationship: user.emergencyContact?.relationship || user.emergencyContactRelationship || '',
      phone: user.emergencyContact?.phone || user.emergencyContactPhone || '',
      address: user.emergencyContact?.address || user.emergencyContactAddress || '',
      region: inferredEmergencyRegion,
      province: emergencyProvince,
      city: user.emergencyContact?.city || user.emergencyContactCity || '',
      barangay: user.emergencyContact?.barangay || user.emergencyContactBarangay || '',
      postalCode: user.emergencyContact?.postalCode || user.emergencyContactPostalCode || '',
      address1: user.emergencyContact?.address1 || user.emergencyContact?.addressLine1 || user.emergencyContactAddressLine1 || '',
    },

    profilePicture: resolveBackendAssetUrl(user.profilePicture, { apiBaseUrl: BASE_URL }),
    signature: resolveBackendAssetUrl(user.signature, { apiBaseUrl: BASE_URL }),
    id: user.id,
    isApproved: user.isApproved
  };
};

function Protected({ isAuthenticated, children }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminDashboardRoute() {
  const outletContext = useOutletContext();
  const onNavigate = outletContext?.onNavigate;
  return <AdminDashboard onNavigate={onNavigate} />;
}

function ProtectedAdminRoute({ children, requiredRole }) {
  if (!isAdminHostAllowed()) {
    return <Navigate to="/" replace />;
  }

  const session = getStoredAdminSession();
  if (!session) {
    clearAdminSession();
    return <Navigate to="/admin/login" replace />;
  }

  try {
    const adminUser = session.adminUser;
    if (requiredRole) {
      const normalizedRequiredRole = String(requiredRole).toUpperCase();
      const roles = Array.isArray(adminUser.roles)
        ? adminUser.roles
            .map((role) => (typeof role === 'object' ? (role?.code ?? role?.name) : role))
            .map((role) => String(role ?? '').trim().toUpperCase())
            .filter(Boolean)
        : [];
      if (!roles.includes(normalizedRequiredRole)) {
        return <Navigate to="/admin/dashboard" replace />;
      }
    }
    return children;
  } catch {
    return <Navigate to="/admin/login" replace />;
  }
}

function AdminHostGuard({ children }) {
  if (!isAdminHostAllowed()) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AdminLoginRoute() {
  const session = getStoredAdminSession();
  if (session) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <AdminLogin />;
}

function ProtectedDepartmentRoute({ children }) {
  const deptUserStr = localStorage.getItem('deptUser');
  if (!deptUserStr) {
    return <Navigate to="/departments/login" replace />;
  }
  try {
    JSON.parse(deptUserStr);
    return children;
  } catch {
    return <Navigate to="/departments/login" replace />;
  }
}

function PageWithTitle({ title, children }) {
  usePageTitle(title);
  return children;
}

function SessionExpiredModal({ open, onAcknowledge, autoDismissMs = 3500 }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onAcknowledge?.(navigate);
    }, autoDismissMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoDismissMs, navigate, onAcknowledge, open]);

  if (!open) {
    return null;
  }

  return (
    <div className="layout-confirm" role="dialog" aria-modal="true" aria-label="Session expired">
      <div className="layout-confirm__backdrop" />
      <div className="layout-confirm__panel">
        <h3>Session Expired</h3>
        <p>Session expired. Please log in again.</p>
        <div className="layout-confirm__actions">
          <button
            type="button"
            className="layout-confirm__button layout-confirm__button--danger"
            onClick={() => onAcknowledge?.(navigate)}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [userAuth, setUserAuth] = useState(() => restoreStoredUserAuth());
  const [user, setUser] = useState(() => userAuth.user);
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);
  const userRef = useRef(user);
  const userAuthRef = useRef(userAuth);
  const pendingSyncRequestRef = useRef(null);
  const sessionExpirationAcknowledgeInFlightRef = useRef(false);
  const memberIdleTimerRef = useRef(null);
  const memberLastActivityAtRef = useRef(Date.now());

  useEffect(() => {
    userRef.current = user;
    userAuthRef.current = userAuth;
  }, [user, userAuth]);

  useEffect(() => {
    persistUserAuth({
      user,
      token: userAuth?.token ?? null,
      rememberMe: Boolean(userAuth?.rememberMe),
      expiresAt: userAuth?.expiresAt ?? null,
    });
  }, [user, userAuth?.expiresAt, userAuth?.rememberMe, userAuth?.token]);

  useEffect(() => {
    const unsubscribe = onMemberSessionExpired(() => {
      const hasMemberAuth = Boolean(userRef.current || userAuthRef.current?.token);
      if (!hasMemberAuth) {
        return;
      }

      setSessionExpiredModalOpen(true);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const applyAuthIfChanged = (incomingAuth) => {
      if (!incomingAuth?.user) return;

      const currentSignature = serializeAuthState({
        user: userRef.current,
        token: userAuthRef.current?.token ?? null,
        rememberMe: Boolean(userAuthRef.current?.rememberMe),
        expiresAt: userAuthRef.current?.expiresAt ?? null,
      });

      const incomingSignature = serializeAuthState(incomingAuth);
      if (incomingSignature === currentSignature) {
        return;
      }

      setUserAuth({
        user: incomingAuth.user,
        token: incomingAuth.token ?? null,
        rememberMe: Boolean(incomingAuth.rememberMe),
        expiresAt: incomingAuth.expiresAt ?? null,
      });
      setUser(incomingAuth.user);
    };

    const onStorage = (event) => {
      if (!event.key) return;

      if (event.key === USER_AUTH_SYNC_LOGOUT_KEY) {
        setUserAuth({ user: null, token: null, rememberMe: false, expiresAt: null });
        setUser(null);
        return;
      }

      if (event.key === USER_AUTH_SYNC_REQUEST_KEY && event.newValue) {
        if (!userRef.current) return;

        try {
          const request = JSON.parse(event.newValue);
          const response = {
            requestId: request?.requestId ?? null,
            auth: {
              user: userRef.current,
              token: userAuthRef.current?.token ?? null,
              rememberMe: Boolean(userAuthRef.current?.rememberMe),
              expiresAt: userAuthRef.current?.expiresAt ?? null,
            },
            ts: Date.now(),
          };
          localStorage.setItem(USER_AUTH_SYNC_RESPONSE_KEY, JSON.stringify(response));
        } catch (_error) {
        }
        return;
      }

      if (event.key === USER_AUTH_SYNC_RESPONSE_KEY && event.newValue) {
        try {
          const response = JSON.parse(event.newValue);
          const requestId = pendingSyncRequestRef.current;
          const isMatch = !requestId || response?.requestId === requestId;
          if (!isMatch) return;
          applyAuthIfChanged(response?.auth);
        } catch (_error) {
        }
      }
    };

    window.addEventListener('storage', onStorage);

    if (!userRef.current) {
      const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      pendingSyncRequestRef.current = requestId;
      localStorage.setItem(
        USER_AUTH_SYNC_REQUEST_KEY,
        JSON.stringify({ requestId, ts: Date.now() })
      );

      setTimeout(() => {
        if (pendingSyncRequestRef.current === requestId) {
          pendingSyncRequestRef.current = null;
        }
      }, 1500);
    }

    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fetch fresh user data on mount if user exists
  useEffect(() => {
    const fetchUser = async () => {
      if (user?.id) {
        try {
          const { data } = await getUserProfile(user.id);
          if (data.user) {
            setUser(prev => ({ ...prev, ...data.user }));
          }
        } catch (error) {
          console.error('Failed to refresh user profile:', error);
          // Optional: handle token expiration or user deletion
        }
      }
    };
    fetchUser();
  }, [user?.id]);

  // Keep branding fresh so admin logo/design updates appear quickly for active users.
  useEffect(() => {
    let cancelled = false;

    const loadBranding = async () => {
      try {
        const resp = await fetch(`${BASE_URL}/api/union-data/branding?ts=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!resp.ok || cancelled) {
          return;
        }
        const data = await resp.json();
        if (!cancelled && data?.affiliations) {
          setBrandingData(data.affiliations);
        }
      } catch (_err) {
        // Silently fall back to static bundled logos
      }
    };

    const onWindowFocus = () => {
      loadBranding();
    };

    loadBranding();
    const interval = window.setInterval(loadBranding, 30000);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, []);

  const [dues] = useState(mockDues);
  const [news] = useState(mockNews);

  const isAuthenticated = Boolean(user);

  const clearMemberIdleTimer = useCallback(() => {
    if (memberIdleTimerRef.current) {
      window.clearTimeout(memberIdleTimerRef.current);
      memberIdleTimerRef.current = null;
    }
  }, []);

  const scheduleMemberIdleTimer = useCallback(() => {
    clearMemberIdleTimer();

    if (!isAuthenticated || sessionExpiredModalOpen) {
      return;
    }

    const elapsedMs = Date.now() - memberLastActivityAtRef.current;
    const timeoutMs = Math.max(0, MEMBER_IDLE_TIMEOUT_MS - elapsedMs);

    memberIdleTimerRef.current = window.setTimeout(() => {
      const hasMemberAuth = Boolean(userRef.current || userAuthRef.current?.token);
      if (!hasMemberAuth || sessionExpirationAcknowledgeInFlightRef.current) {
        return;
      }

      setSessionExpiredModalOpen(true);
    }, timeoutMs);
  }, [clearMemberIdleTimer, isAuthenticated, sessionExpiredModalOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearMemberIdleTimer();
      return undefined;
    }

    const recordActivity = () => {
      if (sessionExpiredModalOpen) {
        return;
      }
      memberLastActivityAtRef.current = Date.now();
      scheduleMemberIdleTimer();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recordActivity();
      }
    };

    memberLastActivityAtRef.current = Date.now();
    scheduleMemberIdleTimer();

    window.addEventListener('pointerdown', recordActivity, { passive: true });
    window.addEventListener('keydown', recordActivity, { passive: true });
    window.addEventListener('scroll', recordActivity, { passive: true });
    window.addEventListener('touchstart', recordActivity, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearMemberIdleTimer();
      window.removeEventListener('pointerdown', recordActivity);
      window.removeEventListener('keydown', recordActivity);
      window.removeEventListener('scroll', recordActivity);
      window.removeEventListener('touchstart', recordActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearMemberIdleTimer, isAuthenticated, scheduleMemberIdleTimer, sessionExpiredModalOpen]);

  const handleLogout = useCallback(() => {
    clearUserAuthStorage();
    localStorage.setItem(USER_AUTH_SYNC_LOGOUT_KEY, String(Date.now()));
    setUserAuth({ user: null, token: null, rememberMe: false, expiresAt: null });
    setUser(null);
  }, []);

  const handleAcknowledgeSessionExpiration = useCallback((navigate) => {
    if (sessionExpirationAcknowledgeInFlightRef.current) {
      return;
    }

    sessionExpirationAcknowledgeInFlightRef.current = true;
    setSessionExpiredModalOpen(false);
    handleLogout();

    if (typeof navigate === 'function') {
      navigate('/login', { replace: true });
    } else if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }

    window.setTimeout(() => {
      sessionExpirationAcknowledgeInFlightRef.current = false;
    }, 0);
  }, [handleLogout]);

  const DashboardRoute = () => (
    <DashboardPage
      user={user}
      dues={dues}
      news={news}
      onLogout={handleLogout}
    />
  );

  const DigitalIdRoute = () => <DigitalIdPage user={user} onLogout={handleLogout} />;

  const DuesRoute = () => <DuesTrackingPage user={user} dues={dues} onLogout={handleLogout} />;

  const NewsRoute = () => <NewsPage user={user} news={news} onLogout={handleLogout} />;

  const AccountRoute = () => <AccountPage user={user} onLogout={handleLogout} />;

  const BenefitsRoute = () => <BenefitsPage user={user} onLogout={handleLogout} />;

  const MembershipFormRoute = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const normalizedStatus = String(user?.isApproved || '').toLowerCase();
    const isReapplication = normalizedStatus === 'rejected';
    const isPendingView = normalizedStatus === 'pending';

    const initialData = useMemo(() => mapUserToRegistrationInitialData(user), []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReapply = async (formData) => {
      setError('');
      setLoading(true);
      try {
        const payload = {
          id: user.id,
          firstName: formData.firstName,
          middleInitial: formData.middleInitial,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          placeOfBirth: formData.placeOfBirth,
          region: formData.region || initialData?.region || '',
          province: formData.province,
          city: formData.city,
          barangay: formData.barangay || formData.address_line2 || '',
          addressLine1: formData.address_line1,
          addressLine2: formData.address_line2 || formData.barangay || '',
          zipCode: formData.postal_code,
          email: formData.email,
          phone: formData.phone,
          religion: formData.religion,
          maritalStatus: formData.maritalStatus,
          numberOfChildren: formData.numberOfChildren,
          education: formData.education,
          gender: formData.gender,
          genderOther: formData.genderOther || formData.otherGender || null,
          company: formData.company,
          position: formData.position,
          yearsEmployed: formData.yearsEmployed,
          department: formData.department,
          unionAffiliation: formData.unionAffiliation,
          unionPosition: formData.unionPosition,
          emergencyName: formData.emergencyContact?.name,
          emergencyRelationship: formData.emergencyContact?.relationship,
          emergencyAddress: formData.emergencyContact?.address,
          emergencyPhone: formData.emergencyContact?.phone,
          emergencyRegion: formData.emergencyContact?.region,
          emergencyProvince: formData.emergencyContact?.province,
          emergencyCity: formData.emergencyContact?.city,
          emergencyBarangay: formData.emergencyContact?.barangay,
          emergencyPostalCode: formData.emergencyContact?.postalCode,
          emergencyAddressLine1: formData.emergencyContact?.addressLine1,
          signature: formData.signature,
          photo: formData.profilePicture,
        };

        await client.post('/api/auth/reapply', payload);

        try {
          const { data } = await getUserProfile(user.id);
          if (data.user) {
            setUser((prev) => ({ ...prev, ...data.user }));
          }
        } catch (fetchErr) {
          console.error('Failed to fetch fresh profile after reapply:', fetchErr);
        }

        navigate('/dashboard');
      } catch (err) {
        setError(
          err.response?.data?.error
            ? `${err.response.data.message} (${err.response.data.error})`
            : err.response?.data?.message || 'Reapplication failed. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    if (isReapplication) {
      return (
        <RegistrationPage
          initialData={initialData}
          onSubmit={handleReapply}
          onBack={() => navigate('/dashboard')}
          submitting={loading}
          submitError={error}
        />
      );
    }

    if (isPendingView) {
      return (
        <RegistrationPage
          initialData={initialData}
          onSubmit={() => {}}
          onBack={() => navigate('/dashboard')}
          submitting={false}
          submitError=""
          readOnly
          initialStep={6}
        />
      );
    }

    return (
      <MembershipFormPage
        user={user}
        onLogout={handleLogout}
      />
    );
  };

  const CompleteProfileRoute = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Transform user object to form state structure if needed
    const initialData = useMemo(() => mapUserToRegistrationInitialData(user), []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdate = async (formData) => {
      setError('');
      setLoading(true);
      try {
        await updateUserProfile(user.id, formData);
        // Fetch fresh user data to ensure status and other fields are up to date
        try {
          const { data } = await getUserProfile(user.id);
          if (data.user) {
            setUser(prev => ({ ...prev, ...data.user }));
          } else {
            setUser(prev => ({ ...prev, ...formData }));
          }
        } catch (fetchErr) {
          console.error('Failed to fetch fresh profile after update:', fetchErr);
          setUser(prev => ({ ...prev, ...formData }));
        }
        navigate('/dashboard');
      } catch (err) {
        setError(
          err.response?.data?.error
            ? `${err.response.data.message} (${err.response.data.error})`
            : err.response?.data?.message || 'Update failed. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    return (
      <RegistrationPage
        initialData={initialData}
        onSubmit={handleUpdate}
        onBack={() => navigate('/dashboard')}
        submitting={loading}
        submitError={error}
      />
    );
  };

  const RequestAssistanceRoute = () => <RequestAssistancePage user={user} onLogout={handleLogout} />;
  const MyRequestsRoute = () => <MyRequestsPage user={user} onLogout={handleLogout} />;
  const TicketDetailRoute = () => <TicketDetailPage user={user} onLogout={handleLogout} />;

  const VenueBrowseRoute = () => <VenueBrowsePage user={user} onLogout={handleLogout} />;
  const VenueDetailRoute = () => <VenueDetailPage user={user} onLogout={handleLogout} />;
  const MyVenueBookingsRoute = () => <MyVenueBookingsPage user={user} onLogout={handleLogout} />;
  const NotificationsRoute = () => <NotificationsPage user={user} onLogout={handleLogout} />;

  const LandingRoute = () => {
    const navigate = useNavigate();
    if (isAuthenticated) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <LandingPage
        onGetStarted={() => navigate('/human-check', {
          state: { nextPath: '/register', requiredAction: 'register_request_code' },
        })}
        onLogin={() => navigate('/human-check', {
          state: { nextPath: '/login', requiredAction: 'login_start' },
        })}
      />
    );
  };

  const LoginRoute = () => {
    const navigate = useNavigate();

    if (isAuthenticated) {
      return <Navigate to="/dashboard" replace />;
    }

    return (
      <LoginPage
        onLoginSuccess={(userData) => {
          const resolvedUser = userData?.user ?? userData;
          const rememberMe = Boolean(userData?.rememberMe);
          const fallbackToken = localStorage.getItem(USER_LEGACY_TOKEN_KEY);
          const nextAuth = {
            user: resolvedUser,
            token: userData?.token ?? fallbackToken ?? null,
            rememberMe,
            expiresAt: userData?.expiresAt ?? (rememberMe ? new Date(Date.now() + REMEMBER_ME_DURATION_MS).toISOString() : null),
          };
          setUserAuth(nextAuth);
          setUser(resolvedUser);
          localStorage.setItem(USER_AUTH_SYNC_RESPONSE_KEY, JSON.stringify({
            requestId: null,
            auth: nextAuth,
            ts: Date.now(),
          }));
          navigate('/dashboard');
        }}
        onBack={() => navigate('/')}
        onCreateAccount={() => navigate('/human-check', {
          state: { nextPath: '/register', requiredAction: 'register_request_code' },
        })}
      />
    );
  };

  const RegistrationRoute = () => {
    const navigate = useNavigate();

    if (isAuthenticated) {
      return <Navigate to="/dashboard" replace />;
    }

    const handleAutoLogin = (userData) => {
      const resolvedUser = userData?.user ?? userData;
      const fallbackToken = localStorage.getItem(USER_LEGACY_TOKEN_KEY);
      const nextAuth = {
        user: resolvedUser,
        token: userData?.token ?? fallbackToken ?? null,
        rememberMe: Boolean(userData?.rememberMe),
        expiresAt: userData?.expiresAt ?? null,
      };
      setUserAuth(nextAuth);
      setUser(resolvedUser);
      localStorage.setItem(USER_AUTH_SYNC_RESPONSE_KEY, JSON.stringify({
        requestId: null,
        auth: nextAuth,
        ts: Date.now(),
      }));
      navigate('/dashboard');
    };

    return (
      <SimpleRegistrationPage
        onBack={() => navigate('/')}
        onAutoLogin={handleAutoLogin}
      />
    );
  };

  const RegistrationSuccessRoute = () => {
    const navigate = useNavigate();
    return (
      <RegistrationSuccessPage
        onContinue={() => {
          if (user) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
        }}
      />
    );
  };

  const GoogleAuthCallbackRoute = () => {
    const navigate = useNavigate();
    return (
      <GoogleAuthCallback
        onSuccess={(userData) => {
          const resolvedUser = userData?.user ?? userData;
          const fallbackToken = localStorage.getItem(USER_LEGACY_TOKEN_KEY);
          const nextAuth = {
            user: resolvedUser,
            token: userData?.token ?? fallbackToken ?? null,
            rememberMe: Boolean(userData?.rememberMe),
            expiresAt: userData?.expiresAt ?? null,
          };
          setUserAuth(nextAuth);
          setUser(resolvedUser);
          localStorage.setItem(USER_AUTH_SYNC_RESPONSE_KEY, JSON.stringify({
            requestId: null,
            auth: nextAuth,
            ts: Date.now(),
          }));
          navigate('/dashboard');
        }}
      />
    );
  };

  const defaultRoute = useMemo(() => (isAuthenticated ? '/dashboard' : '/'), [isAuthenticated]);

  const withTitle = (element, title) => (
    <PageWithTitle title={title}>
      {element}
    </PageWithTitle>
  );

  return (
    <Router>
      <Routes>
        <Route path="/" element={withTitle(<LandingRoute />, 'LaborConnect | Welcome')} />
        <Route path="/human-check" element={withTitle(<HumanVerificationPage />, 'LaborConnect | Security Check')} />
        <Route path="/login" element={withTitle(<LoginRoute />, 'LaborConnect | Member Login')} />
        <Route path="/oauth/google" element={withTitle(<GoogleAuthCallbackRoute />, 'LaborConnect | Google Login')} />
        <Route path="/register" element={withTitle(<RegistrationRoute />, 'LaborConnect | Register')} />
        <Route path="/terms-and-conditions" element={withTitle(<TermsAndConditionsPage />, 'LaborConnect | Terms & Conditions')} />
        <Route path="/privacy-policy" element={withTitle(<PrivacyPolicyPage />, 'LaborConnect | Data Privacy Policy')} />
        <Route path="/forgot-password" element={withTitle(<ForgotPasswordPage />, 'LaborConnect | Forgot Password')} />
        <Route path="/reset-password" element={withTitle(<ResetPasswordPage />, 'LaborConnect | Reset Password')} />
        <Route path="/registration-success" element={withTitle(<RegistrationSuccessRoute />, 'LaborConnect | Registration Success')} />
        <Route path="/scanqr" element={withTitle(<ScanQrPage />, 'LaborConnect | Scan QR')} />
        <Route path="/scan-qr" element={withTitle(<ScanQrPage />, 'LaborConnect | Scan QR')} />
        <Route path="/payment/success" element={withTitle(<PaymentSuccessPage />, 'LaborConnect | Payment Success')} />
        <Route path="/payment/cancel" element={withTitle(<PaymentCancelPage />, 'LaborConnect | Payment Canceled')} />
        <Route
          path="/dashboard"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <DashboardRoute />
            </Protected>,
            'LaborConnect | Dashboard'
          )}
        />
        <Route
          path="/digital-id"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <DigitalIdRoute />
            </Protected>,
            'LaborConnect | Digital ID'
          )}
        />
        <Route
          path="/dues"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <DuesRoute />
            </Protected>,
            'LaborConnect | Dues Tracking'
          )}
        />
        <Route
          path="/news"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <NewsRoute />
            </Protected>,
            'LaborConnect | Events'
          )}
        />
        <Route
          path="/account"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <AccountRoute />
            </Protected>,
            'LaborConnect | Account'
          )}
        />
        <Route
          path="/benefits"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <BenefitsRoute />
            </Protected>,
            'LaborConnect | Benefits'
          )}
        />
        <Route
          path="/membership-form"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <MembershipFormRoute />
            </Protected>,
            'LaborConnect | Membership Form'
          )}
        />
        <Route
          path="/request-assistance"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <RequestAssistanceRoute />
            </Protected>,
            'LaborConnect | Request Assistance'
          )}
        />
        <Route
          path="/my-requests"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <MyRequestsRoute />
            </Protected>,
            'LaborConnect | My Requests'
          )}
        />
        <Route
          path="/my-requests/:ticketId"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <TicketDetailRoute />
            </Protected>,
            'LaborConnect | Ticket Details'
          )}
        />
        <Route
          path="/venues"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <VenueBrowseRoute />
            </Protected>,
            'LaborConnect | Venue Booking'
          )}
        />
        <Route
          path="/venues/:venueId"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <VenueDetailRoute />
            </Protected>,
            'LaborConnect | Venue Details'
          )}
        />
        <Route
          path="/my-venue-bookings"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <MyVenueBookingsRoute />
            </Protected>,
            'LaborConnect | Booked Events'
          )}
        />
        <Route
          path="/notifications"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <NotificationsRoute />
            </Protected>,
            'LaborConnect | Notifications'
          )}
        />
        <Route
          path="/complete-profile"
          element={withTitle(
            <Protected isAuthenticated={isAuthenticated}>
              <CompleteProfileRoute />
            </Protected>,
            'LaborConnect | Complete Profile'
          )}
        />
        <Route
          path="/admin/login"
          element={withTitle(
            <AdminHostGuard>
              <AdminLoginRoute />
            </AdminHostGuard>,
            'LaborConnect | Admin Login'
          )}
        />
        <Route
          path="/admin/forgot-password"
          element={withTitle(
            <AdminHostGuard>
              <AdminForgotPassword />
            </AdminHostGuard>,
            'LaborConnect | Admin Password Reset'
          )}
        />
        <Route
          path="/admin/reset-password"
          element={withTitle(
            <AdminHostGuard>
              <AdminResetPassword />
            </AdminHostGuard>,
            'LaborConnect | Admin Reset Password'
          )}
        />
        <Route
          path="/admin"
          element={withTitle(
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>,
            'LaborConnect | Admin Console'
          )}
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardRoute />} />
          <Route path="members" element={<MembersTable />} />
          <Route path="members/:memberId" element={<MemberProfile />} />
          <Route path="registration-review" element={<RegistrationReview />} />
          <Route path="proponent-queue" element={<ProponentQueue />} />
          <Route path="final-approval-queue" element={<AdminFinalApprovalQueue />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="ticket-detail" element={<TicketDetail />} />
          <Route path="reports-analytics" element={<ReportsAnalytics />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="event-management" element={<EventManagement />} />
          <Route path="venue-management" element={<VenueManagement />} />
          <Route path="client-bookings" element={<ClientBookings />} />
          <Route path="id-card-management" element={<IDCardManagement />} />
          <Route
            path="admin-settings"
            element={
              <ProtectedAdminRoute requiredRole="SUPER_ADMIN">
                <AdminSettings />
              </ProtectedAdminRoute>
            }
          />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>
        <Route path="/departments/login" element={withTitle(<DepartmentLogin />, 'LaborConnect | Department Login')} />
        <Route
          path="/departments"
          element={withTitle(
            <ProtectedDepartmentRoute>
              <DepartmentLayout />
            </ProtectedDepartmentRoute>,
            'LaborConnect | Department Portal'
          )}
        >
          <Route index element={<Navigate to="tickets" replace />} />
          <Route path="tickets" element={<DepartmentTickets />} />
          <Route path="ticket-detail" element={<DepartmentTicketDetail />} />
        </Route>
        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
      <ScrollToTopButton />
      <SessionExpiredModal
        open={sessionExpiredModalOpen}
        onAcknowledge={handleAcknowledgeSessionExpiration}
      />
    </Router>
  );
}

export default App;
